import { AtomSpinner } from './Spinners/Atom.js'

export class Wait {
	private overlay: HTMLElement | null = null
	private spinner: AtomSpinner = new AtomSpinner()
	private activeFormIds: Set<string> = new Set()
	private pendingResubmits: WeakSet<HTMLFormElement> = new WeakSet()
	private isActivated: boolean = false

	private resolveOverlay(): HTMLElement | null {
		if (this.overlay && document.contains(this.overlay)) {
			return this.overlay
		}

		this.overlay = document.querySelector<HTMLElement>('.wait-overlay')
			?? document.getElementById('magic-pin-login-overlay')
		return this.overlay
	}

	private resolveSpinnerCanvas(): HTMLCanvasElement | null {
		const overlay = this.resolveOverlay()
		if (!overlay) {
			return null
		}

		let canvas = overlay.querySelector<HTMLCanvasElement>('canvas')
		if (!canvas) {
			canvas = document.createElement('canvas')
			canvas.className = 'wait-spinner'
			canvas.setAttribute('aria-hidden', 'true')
			canvas.setAttribute('role', 'presentation')
			overlay.appendChild(canvas)
		}

		return canvas
	}

	public show(): boolean {
		const overlay = this.resolveOverlay()
		if (!overlay) {
			return false
		}

		overlay.classList.remove('d-none')
		overlay.setAttribute('aria-hidden', 'false')
		const color = this.resolveSpinnerColor()
		this.spinner.setColor(color)
		return true
	}

	private resolveSpinnerColor(): string {
		const color = getComputedStyle(document.documentElement).getPropertyValue('--bs-primary').trim()
		return color
	}

	public hide(): boolean {
		const overlay = this.resolveOverlay()
		if (!overlay) {
			return false
		}

		this.spinner.stop()
		overlay.classList.add('d-none')
		overlay.setAttribute('aria-hidden', 'true')
		return true
	}

	public activate(formId: string = 'magic-pin-login-form'): boolean {
		this.activeFormIds.add(formId)
		this.resolveOverlay()
		this.resolveSpinnerCanvas()

		if (this.isActivated) {
			return true
		}

		const handleSubmit = (event: Event, form: HTMLFormElement, submitter?: HTMLElement): void => {
			if (this.pendingResubmits.has(form)) {
				this.pendingResubmits.delete(form)
				return
			}

			event.preventDefault()
			this.show()

			this.pendingResubmits.add(form)
			window.setTimeout(() => {
				if (!document.contains(form)) {
					this.pendingResubmits.delete(form)
					return
				}

				try {
					if (submitter instanceof HTMLElement) {
						form.requestSubmit(submitter)
					}
					else {
						form.requestSubmit()
					}
				}
				catch {
					this.pendingResubmits.delete(form)
					form.submit()
				}
			}, 0)
		}

		document.addEventListener('click', (event) => {
			const target = event.target
			if (!(target instanceof HTMLElement)) {
				return
			}

			const form = target.closest('form')
			if (!(form instanceof HTMLFormElement) || !this.activeFormIds.has(form.id)) {
				return
			}

			const isSubmitControl = target instanceof HTMLButtonElement
				? target.type === 'submit'
				: target instanceof HTMLInputElement && target.type === 'submit'

			if (!isSubmitControl) {
				return
			}

			handleSubmit(event, form, target)
		}, true)

		document.addEventListener('submit', (event) => {
			const form = event.target
			if (!(form instanceof HTMLFormElement) || !this.activeFormIds.has(form.id)) {
				return
			}

			const submitter = event instanceof SubmitEvent ? event.submitter : undefined
			handleSubmit(event, form, submitter instanceof HTMLElement ? submitter : undefined)
		}, true)

		this.isActivated = true
		return true
	}
}
